import { useCallback, useMemo } from 'react';
import {
  generateChallenge,
  generateCodeVerifier,
} from '../utils/authSessionUtils';
import {
  IPiNxtAuthCodeAPIResponse,
  IPiNxtTokenAPIResponse,
} from '../../types/pinxt';
import { restFetcher } from '../../services/restFetcher';
import { useAppConfig } from '../../hooks/useAppConfig';

const CONTENT_TYPE_FORM_ENCODED = 'application/x-www-form-urlencoded';

/**
 * Manages all the API calls directly to PiNXt for Authentication.
 * Don't use directly on Page Level Components, use useAuthSession hook instead.
 */
export const useAuthService = () => {
  const {
    api: { auth: authHost },
    auth: { targetBg, piClientId },
  } = useAppConfig();

  const codeVerifier = useMemo(generateCodeVerifier, []);

  /**
   * PiNXt Auth Step 1 of 2: Requesting auth code
   * @param username
   * @param password
   * @param codeVerifier
   * @param baseAuthUrl
   */
  const fetchAuthCode = useCallback(
    async (
      username: string,
      password: string
    ): Promise<IPiNxtAuthCodeAPIResponse> => {
      const codeChallenge = await generateChallenge(codeVerifier);
      const auth = window.btoa(`${username}:${password}`);
      const endpoint = `${authHost}/enterprise/portals/password/auth`;

      const qsParams = [
        `client_id=${piClientId}`,
        `response_type=code`,
        `state=state`,
        `code_challenge_method=S512`,
        `code_challenge=${codeChallenge}`,
      ];

      return restFetcher({
        endpoint,
        method: 'POST',
        requestBody: qsParams.join('&'),
        targetBg,
        additionalHeaders: {
          'Content-Type': CONTENT_TYPE_FORM_ENCODED,
          Authorization: `Basic ${auth}`, // requires Basic
        },
        onResponse: (responseBody) => ({
          code: responseBody.resultCode,
          message: responseBody.resultMessage,
        }),
      });
    },
    [authHost, codeVerifier, piClientId, targetBg]
  );

  /**
   * PiNXt Auth Step 2 of 2: Requesting auth tokens (authToken, IdToken, etc.)
   * @param authCode
   * @param codeVerifier
   * @param baseAuthUrl
   */
  const fetchTokens = useCallback(
    async (authCode: string): Promise<IPiNxtTokenAPIResponse> => {
      const endpoint = `${authHost}/token`;

      const qsParams = [
        `client_id=${piClientId}`,
        `grant_type=authorization_code`,
        `code=${authCode}`,
        `code_verifier=${codeVerifier}`,
      ];

      return restFetcher({
        endpoint,
        method: 'POST',
        requestBody: qsParams.join('&'),
        targetBg,
        additionalHeaders: {
          'Content-Type': CONTENT_TYPE_FORM_ENCODED,
        },
        onResponse: (responseBody) => ({
          code: responseBody.resultCode,
          message: responseBody.resultMessage,
        }),
      });
    },
    [authHost, codeVerifier, piClientId, targetBg]
  );

  /**
   * Grabs auth token for new session
   * @param refreshToken
   * @param baseAuthUrl
   */
  const fetchTokensOnRefresh = useCallback(
    async (refreshToken: string): Promise<IPiNxtTokenAPIResponse> => {
      const endpoint = `${authHost}/token`;

      const qsParams = [
        `client_id=${piClientId}`,
        `grant_type=refresh_token`,
        `refresh_token=${refreshToken}`,
      ];

      return restFetcher({
        endpoint,
        method: 'POST',
        requestBody: qsParams.join('&'),
        targetBg,
        additionalHeaders: {
          'Content-Type': CONTENT_TYPE_FORM_ENCODED,
        },
        onResponse: (responseBody) => ({
          code: responseBody.resultCode,
          message: responseBody.resultMessage,
        }),
      });
    },
    [authHost, piClientId, targetBg]
  );

  /**
   * Logs out of current session
   * @param accessToken
   * @param baseAuthUrl
   */
  const fetchLogout = useCallback(
    async (accessToken: string): Promise<IPiNxtTokenAPIResponse> =>
      restFetcher({
        endpoint: `${authHost}/logout/local`,
        method: 'POST',
        accessToken,
        targetBg,
        additionalHeaders: {
          'Content-Type': 'application/json',
        },
        onResponse: (responseBody) => ({
          code: responseBody.resultCode,
          message: responseBody.resultMessage,
        }),
      }),
    [authHost, targetBg]
  );

  return { fetchAuthCode, fetchTokens, fetchTokensOnRefresh, fetchLogout };
};
